import { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { FiservCardInterface } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { OptionKey, PaginationParams } from '@/common/types';
import { ROOT_DEPENDENT_ID } from '@/modules/transaction/Cards/hooks/useCardFormatting';
import { useSearchParams } from '@/modules/transaction/Cards/hooks/useSearchParams';
import { useGetCardsQuery } from '@/modules/transaction/Cards/queries/useGetCards.query';
import { useGetCardTransactionList } from '@/modules/transaction/TransactionsList/query/useGetCardTransactionList.query';

interface CurrentPaginationParams extends PaginationParams {
  statuses?: OptionKey[];
  startDate?: Date | null;
  endDate?: Date | null;
}

interface CardTransaction {
  claimId: number;
  amount: number;
  title: string;
  date: string;
  status: string;
  category: string;
  suppressed: boolean;
}

const isRefund = (swiped_amount?: number) => (swiped_amount ? swiped_amount > 0 : false);
const REFUND = 'REFUND';

const useCardTransactionsList = ({
  page,
  perPage,
  searchString,
  statuses,
  startDate,
  endDate,
}: CurrentPaginationParams) => {
  const { query: cardId, removeParams } = useSearchParams('card_id');
  const {
    params: { id: employeeId },
  } = useRouteMatch<{ id: string }>();

  const { cardMap } = useGetCardsQuery(employeeId);

  const allCards: FiservCardInterface[] = useMemo(
    () =>
      Object.values(cardMap).flatMap((holderCards) =>
        Object.values(holderCards).flat() as FiservCardInterface[]
      ),
    [cardMap]
  );

  const activeCard = useMemo(() => allCards.find((card) => card.id === cardId), [cardId, allCards]);
  const holderName = useMemo(() => activeCard?.holder?.name || '', [activeCard]);
  const isRootCard = useMemo(() => activeCard?.holder?.dependentId === ROOT_DEPENDENT_ID, [activeCard]);

  const { isLoading, data, total } = useGetCardTransactionList({
    cardId,
    perPage,
    page,
    employeeId,
    statuses: statuses as string[],
    startDate,
    endDate,
    searchString: searchString || '',
    ...(isRootCard && _.isNumber(activeCard?.cardAccountId) ? { cardAccountId: `${activeCard.cardAccountId}` } : {}),
  });

  const formattedData = useMemo<CardTransaction[]>(() => {
    const newList = data.map((item) => ({
      claimId: item.claim_id,
      amount: isRefund(item?.swiped_amount)
        ? item.swiped_amount
        : item?.approved_amount || item.swiped_amount,
      title: isRefund(item?.swiped_amount) ? `Return ${item.memo}` : item?.memo || '',
      date: item.card_swipe_date ? dayjs(item.card_swipe_date).format(DEFAULT_DATE_FORMAT) : '',
      status: item.claim_status,
      category: isRefund(item?.swiped_amount) ? REFUND : item?.category_type || '',
      suppressed: !!item?.suppressed,
    }));
    return newList;
  }, [data]);

  const removeAllParams = () => removeParams(['card_id']);

  return {
    data: formattedData || [],
    isLoading,
    total,
    activeCard,
    holderName,
    removeAllParams,
  };
};

export default useCardTransactionsList;
